import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import CharacterGuide from "@/components/feature/CharacterGuide";
import { mobilePlans, type MobilePlan } from "@/data/plans.generated";
import { approvedAds } from "@/data/approvedAds";

type Answers = {
  usage: "small" | "medium" | "large" | "heavy" | "extra" | "unlimited" | "unknown" | "";
  call: "any" | "short" | "long" | "";
  support: "online" | "shop" | "";
};

const questions: {
  key: keyof Answers;
  title: string;
  options: { value: string; label: string; hint: string }[];
}[] = [
  {
    key: "usage",
    title: "月にどれくらいデータを使いますか？",
    options: [
      { value: "small", label: "3GB以内", hint: "家ではWi-Fi中心。外では連絡や検索が中心" },
      { value: "medium", label: "10GB以内", hint: "外出先でSNSや短い動画を見る" },
      { value: "large", label: "30GB以内", hint: "動画や音楽を日常的に楽しむ" },
      { value: "heavy", label: "50GB以内", hint: "外出先でも動画を長く見る" },
      { value: "extra", label: "100GB以内", hint: "テザリングや大容量通信も使う" },
      { value: "unlimited", label: "無制限で使いたい", hint: "容量を気にせず使いたい" },
      { value: "unknown", label: "分からない", hint: "月20GBとして候補を仮に整理する" },
    ],
  },
  {
    key: "call",
    title: "電話の使い方は？",
    options: [
      { value: "any", label: "ほぼかけない", hint: "LINEなどのアプリ通話が中心" },
      { value: "short", label: "短い電話をよくかける", hint: "5〜10分程度の連絡が多い" },
      { value: "long", label: "長電話もする", hint: "時間を気にせず話したい" },
    ],
  },
  {
    key: "support",
    title: "手続きや相談はどうしたいですか？",
    options: [
      { value: "online", label: "オンラインでも大丈夫", hint: "自分で申し込みや設定ができる" },
      { value: "shop", label: "店頭で相談したい", hint: "対面で確認できる安心を残したい" },
    ],
  },
];

const usageNeeds: Record<Exclude<Answers["usage"], "">, number> = {
  small: 3,
  medium: 10,
  large: 30,
  heavy: 50,
  extra: 100,
  unlimited: Number.POSITIVE_INFINITY,
  unknown: 20,
};

const fullShopSupport = new Set([
  "楽天モバイル",
  "UQ mobile",
  "Y!mobile",
  "イオンモバイル",
  "ドコモ",
  "au",
  "ソフトバンク",
]);

const shopPriority = new Map([
  ["楽天モバイル", 0],
  ["UQ mobile", 1],
  ["Y!mobile", 2],
]);

function priceWithCall(plan: MobilePlan, call: Answers["call"]) {
  if (call === "short") return plan.base + plan.short;
  if (call === "long") return plan.base + plan.long;
  return plan.base;
}

function capacityLabel(plan: MobilePlan) {
  return plan.gb === null ? "無制限" : `${plan.gb}GB`;
}

function callLabel(plan: MobilePlan, call: Answers["call"]) {
  if (call === "short") {
    if (plan.short === 0 && plan.long === 0) return "指定アプリ利用で国内通話無料";
    if (plan.short === 0) return `${plan.shortMinutes || plan.includedMinutes || 5}分通話込み`;
    return `短時間通話オプション +${plan.short.toLocaleString()}円`;
  }
  if (call === "long") {
    if (plan.long === 0) return "指定アプリ利用で国内通話無料";
    return `かけ放題 +${plan.long.toLocaleString()}円`;
  }
  return "通話オプションなし";
}

function diagnose(answers: Answers) {
  if (!answers.usage || !answers.call || !answers.support) return [];
  const need = usageNeeds[answers.usage];
  const candidates = mobilePlans.filter((plan) => {
    if (plan.status !== "verified" || plan.speedLimited || plan.periodDays) return false;
    if (plan.gb !== null && plan.gb < need) return false;
    if (answers.support === "shop" && !fullShopSupport.has(plan.brand)) return false;
    return true;
  });

  candidates.sort((a, b) => {
    if (answers.support === "shop") {
      const supportOrder = (shopPriority.get(a.brand) ?? 9) - (shopPriority.get(b.brand) ?? 9);
      if (supportOrder !== 0) return supportOrder;
    }
    return priceWithCall(a, answers.call) - priceWithCall(b, answers.call) || a.id.localeCompare(b.id);
  });

  const brands = new Set<string>();
  return candidates
    .filter((plan) => {
      if (brands.has(plan.brand)) return false;
      brands.add(plan.brand);
      return true;
    })
    .slice(0, 3)
    .map((plan) => ({
      plan,
      total: priceWithCall(plan, answers.call),
      reasons: [
        plan.gb === null ? "データ容量の上限を気にせず使える" : `必要量を満たす${plan.gb}GBのプラン`,
        callLabel(plan, answers.call),
        answers.support === "shop" ? "店頭で相談できる" : "オンラインで手続きできる",
      ],
    }));
}

export default function Diagnosis() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({ usage: "", call: "", support: "" });
  const [done, setDone] = useState(false);
  const results = useMemo(() => (done ? diagnose(answers) : []), [done, answers]);
  const current = questions[step];
  const progress = Math.round(((step + (done ? 1 : 0)) / questions.length) * 100);

  const handleSelect = (value: string) => {
    setAnswers((currentAnswers) => ({ ...currentAnswers, [current.key]: value } as Answers));
    if (step < questions.length - 1) {
      window.setTimeout(() => setStep((currentStep) => currentStep + 1), 150);
    } else {
      window.setTimeout(() => setDone(true), 150);
    }
  };

  const reset = () => {
    setStep(0);
    setDone(false);
    setAnswers({ usage: "", call: "", support: "" });
  };

  return (
    <Layout>
      <section className="border-b border-background-300 bg-background-200">
        <div className="mx-auto max-w-[900px] px-4 md:px-6 py-10 md:py-14">
          <div className="text-xs text-secondary-600 font-semibold tracking-widest">料金診断</div>
          <h1 className="mt-2 text-2xl md:text-3xl font-bold text-foreground-900">今の使い方から、見直しの方向を整理</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 leading-7">
            データ量・電話・相談方法の3つに答えるだけ。約1分で、条件に合う候補を整理します。
          </p>
          <p className="mt-3 text-xs font-semibold text-foreground-700">PR｜このページには広告が含まれます。候補の選定・順序は広告提携の有無で変えません。</p>
        </div>
      </section>

      <section className="mx-auto max-w-[900px] px-4 md:px-6 py-10 md:py-14">
        {!done ? (
          <div className="rounded-xl border border-background-300 bg-background-50 p-6 md:p-10">
            {step === 0 && (
              <CharacterGuide className="mb-7" size="sm" message="一番安い会社を決めつけず、データ量・電話・相談方法から候補を整理します。" />
            )}
            <div className="flex items-center justify-between text-xs text-foreground-500">
              <div>質問 {step + 1} / {questions.length}</div>
              <div>{progress}%</div>
            </div>
            <div className="mt-2 h-1.5 bg-background-200 rounded-full overflow-hidden">
              <div className="h-full bg-primary-500 transition-all" style={{ width: `${progress}%` }} />
            </div>

            <h2 className="mt-8 text-xl md:text-2xl font-bold text-foreground-900">{current.title}</h2>
            <div className="mt-6 grid gap-3">
              {current.options.map((option) => {
                const selected = answers[current.key] === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`text-left p-4 md:p-5 rounded-lg border cursor-pointer transition-colors ${selected ? "border-primary-500 bg-primary-50" : "border-background-300 bg-background-100 hover:border-primary-500"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-[15px] font-semibold text-foreground-900">{option.label}</div>
                      <i className={`ri-${selected ? "checkbox-circle-fill text-primary-500" : "arrow-right-line text-foreground-400"}`}></i>
                    </div>
                    <div className="mt-1 text-[13px] text-foreground-600">{option.hint}</div>
                  </button>
                );
              })}
            </div>

            <div className="mt-6 flex items-center justify-between">
              <button onClick={() => setStep(Math.max(0, step - 1))} disabled={step === 0} className="text-sm text-foreground-600 disabled:opacity-40 cursor-pointer whitespace-nowrap">
                ← 戻る
              </button>
              <div className="text-xs text-foreground-500">回答は保存しません</div>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="rounded-xl border border-background-300 bg-background-50 p-6 md:p-8">
              <div className="flex items-center gap-2 text-primary-500">
                <i className="ri-checkbox-circle-fill text-xl"></i>
                <div className="text-sm font-semibold">診断結果</div>
              </div>
              <h2 className="mt-2 text-xl md:text-2xl font-bold text-foreground-900">まず、この3つを比べてみましょう</h2>
              <p className="mt-2 text-sm text-foreground-600 leading-7">
                期間限定のキャンペーンや端末代を除き、通常月額と選んだ通話条件で候補を整理しています。
                {answers.usage === "unknown" && " データ量は月20GBとして仮置きしました。"}
              </p>
              <CharacterGuide className="mt-5" size="sm" message="候補は整理の出発点です。申し込み前に公式サイトで対応端末、利用地域、最新条件を確認してください。" />
            </div>

            {results.length === 0 && (
              <div className="rounded-xl border border-background-300 bg-background-50 p-6 text-sm text-foreground-700">
                この条件で案内できる候補がありません。回答を変更するか、比較表をご覧ください。
              </div>
            )}

            {results.map((result, index) => (
              <div key={result.plan.id} className="rounded-xl border border-background-300 bg-background-50 p-6 md:p-8">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-xs text-primary-500 font-bold">候補 {index + 1}</div>
                    <div className="mt-1 text-xl font-bold text-foreground-900">{result.plan.brand}</div>
                    <div className="mt-0.5 text-sm text-foreground-600">{result.plan.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-foreground-500">選択条件での月額</div>
                    <div className="text-lg font-bold text-foreground-900">{result.total.toLocaleString()}円</div>
                    {result.total !== result.plan.base && <div className="text-[11px] text-foreground-500">基本料 {result.plan.base.toLocaleString()}円＋通話</div>}
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-xs font-semibold text-foreground-700 mb-2">なぜこの候補なのか</div>
                  <ul className="grid sm:grid-cols-2 gap-2 text-sm text-foreground-700">
                    {result.reasons.map((reason) => <li key={reason} className="flex items-start gap-2"><i className="ri-check-line text-primary-500 mt-0.5"></i>{reason}</li>)}
                  </ul>
                </div>

                <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div className="p-3 bg-background-100 rounded-md"><div className="text-foreground-500">データ</div><div className="font-semibold text-foreground-900 mt-1">{capacityLabel(result.plan)}</div></div>
                  <div className="p-3 bg-background-100 rounded-md"><div className="text-foreground-500">通話</div><div className="font-semibold text-foreground-900 mt-1">{callLabel(result.plan, answers.call)}</div></div>
                  <div className="p-3 bg-background-100 rounded-md"><div className="text-foreground-500">相談</div><div className="font-semibold text-foreground-900 mt-1">{result.plan.support}</div></div>
                  <div className="p-3 bg-background-100 rounded-md"><div className="text-foreground-500">確認日</div><div className="font-semibold text-foreground-900 mt-1">{result.plan.checked}</div></div>
                </div>

                <div className="mt-5 flex flex-wrap gap-4 text-sm">
                  <a href={result.plan.url} target="_blank" rel="noreferrer" className="font-semibold text-primary-500 hover:text-primary-600">公式料金ページを確認 <i className="ri-external-link-line"></i></a>
                  {result.plan.supportUrl && <a href={result.plan.supportUrl} target="_blank" rel="noreferrer" className="text-foreground-600 hover:text-primary-500">サポート条件を見る <i className="ri-external-link-line"></i></a>}
                </div>
                {result.plan.brand === "ahamo" && (
                  <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm">
                    <p className="font-bold text-primary-700">PR｜ahamoの申込み</p>
                    <p className="mt-1 text-foreground-700">ドコモからのプラン変更や対象外の申込みは広告の成果条件に含まれません。料金・対応端末・申込条件は公式サイトで確認してください。</p>
                    <div className="mt-2 font-semibold text-primary-600 underline [&_a]:text-primary-600" dangerouslySetInnerHTML={{ __html: approvedAds.ahamo }} />
                  </div>
                )}
                {result.plan.brand === "楽天モバイル" && (
                  <div className="mt-5 rounded-lg border border-primary-200 bg-primary-50 p-4 text-sm">
                    <p className="font-bold text-primary-700">PR｜楽天モバイルの申込み</p>
                    <p className="mt-1 text-foreground-700">通常のRakuten最強プランへの案内です。個別キャンペーンの特典は、このリンク経由で適用されるとは限りません。申込み前に公式サイトで条件をご確認ください。</p>
                    <div className="mt-2 font-semibold text-primary-600 underline [&_a]:text-primary-600" dangerouslySetInnerHTML={{ __html: approvedAds.rakuten }} />
                  </div>
                )}
              </div>
            ))}

            <div className="rounded-xl bg-background-200 border border-background-300 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="text-sm text-foreground-700">条件を変えてもう一度診断できます。</div>
              <div className="flex gap-3">
                <button onClick={reset} className="px-5 py-2.5 rounded-md bg-background-50 border border-background-400 text-foreground-900 text-sm font-semibold hover:border-primary-500 cursor-pointer whitespace-nowrap">もう一度診断</button>
                <Link to="/compare" className="px-5 py-2.5 rounded-md bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 cursor-pointer whitespace-nowrap">比較表で詳細を見る</Link>
              </div>
            </div>

            <p className="text-xs text-foreground-500 leading-6">
              ※ 通信料のみの比較です。端末代、期間限定の割引・還元、ユニバーサルサービス料等は含みません。料金プラン全体の最終確認日：2026年9月20日。
            </p>
          </div>
        )}
      </section>
    </Layout>
  );
}
